import { useEffect, useState } from 'react';
import { Button, Input, Modal, Select } from '@react-apps/design-system';
import {
  TOTVS_PRODUCT_LINK_ACTIONS,
  TOTVS_PROVIDERS,
} from '../../domain/entities/totvs.entity';
import { getTotvsProviderLabel } from '../display';

export interface TotvsProductLinkFilters {
  provider: string;
  providerAccountId: string;
  productExternalId: string;
  offerExternalId: string;
  action: string;
  idAreaInteresse: string;
  codColigada: string;
  codFilial: string;
  idPs: string;
}

export const EMPTY_TOTVS_PRODUCT_LINK_FILTERS: TotvsProductLinkFilters = {
  provider: '',
  providerAccountId: '',
  productExternalId: '',
  offerExternalId: '',
  action: '',
  idAreaInteresse: '',
  codColigada: '',
  codFilial: '',
  idPs: '',
};

const ACTION_LABELS: Record<
  (typeof TOTVS_PRODUCT_LINK_ACTIONS)[number],
  string
> = {
  enroll: 'Matricular no RM',
  ignore: 'Ignorar',
};

const ACTION_OPTIONS = [
  { value: '', label: 'Todos os destinos' },
  ...TOTVS_PRODUCT_LINK_ACTIONS.map((action) => ({
    value: action,
    label: ACTION_LABELS[action],
  })),
];

const PROVIDER_OPTIONS = [
  { value: '', label: 'Todos os gateways' },
  ...TOTVS_PROVIDERS.map((provider) => ({
    value: provider,
    label: getTotvsProviderLabel(provider),
  })),
];

export function countActiveTotvsProductLinkFilters(
  filters: TotvsProductLinkFilters,
): number {
  return Object.values(filters).filter((value) => value.trim()).length;
}

interface TotvsProductLinkFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: TotvsProductLinkFilters;
  onApply: (filters: TotvsProductLinkFilters) => void;
}

/**
 * Um campo por filtro (não busca livre única): `codColigada`/`codFilial`/`idPs`/
 * `idAreaInteresse` são inteiros pequenos que colidiriam entre si e com outros
 * IDs numa busca por substring.
 */
export function TotvsProductLinkFiltersModal({
  open,
  onClose,
  filters,
  onApply,
}: TotvsProductLinkFiltersModalProps) {
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const update = <K extends keyof TotvsProductLinkFilters>(
    key: K,
    value: TotvsProductLinkFilters[K],
  ) => setDraft((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => {
    onApply({
      provider: draft.provider,
      providerAccountId: draft.providerAccountId.trim(),
      productExternalId: draft.productExternalId.trim(),
      offerExternalId: draft.offerExternalId.trim(),
      action: draft.action,
      idAreaInteresse: draft.idAreaInteresse.trim(),
      codColigada: draft.codColigada.trim(),
      codFilial: draft.codFilial.trim(),
      idPs: draft.idPs.trim(),
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Filtrar de-para TOTVS"
      description="Cada campo casa por valor exato — filtra direto no servidor."
      size="lg"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setDraft(EMPTY_TOTVS_PRODUCT_LINK_FILTERS)}
          >
            Limpar
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleApply}>
            Aplicar filtros
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Gateway"
          options={PROVIDER_OPTIONS}
          value={draft.provider}
          onChange={(e) => update('provider', e.target.value)}
        />
        <Input
          label="Conta do gateway"
          placeholder="ID da conta (vazio = todas)"
          value={draft.providerAccountId}
          onChange={(e) => update('providerAccountId', e.target.value)}
        />
        <Input
          label="Produto externo"
          placeholder="ID do produto no gateway"
          value={draft.productExternalId}
          onChange={(e) => update('productExternalId', e.target.value)}
        />
        <Input
          label="Oferta externa"
          placeholder="ID da oferta no gateway"
          value={draft.offerExternalId}
          onChange={(e) => update('offerExternalId', e.target.value)}
        />
        <Select
          label="Destino"
          options={ACTION_OPTIONS}
          value={draft.action}
          onChange={(e) => update('action', e.target.value)}
        />
        <Input
          label="IDAREAINTERESSE"
          value={draft.idAreaInteresse}
          onChange={(e) => update('idAreaInteresse', e.target.value)}
        />
        <Input
          label="IDPS"
          value={draft.idPs}
          onChange={(e) => update('idPs', e.target.value)}
        />
        <Input
          label="CODCOLIGADA"
          type="number"
          value={draft.codColigada}
          onChange={(e) => update('codColigada', e.target.value)}
        />
        <Input
          label="CODFILIAL"
          type="number"
          value={draft.codFilial}
          onChange={(e) => update('codFilial', e.target.value)}
        />
      </div>
    </Modal>
  );
}
